import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { 
  Truck, Clock, AlertTriangle, Activity, TrendingUp, TrendingDown,
  CheckCircle, XCircle, Package, Loader2, RefreshCw
} from 'lucide-react';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, 
  ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, LabelList
} from 'recharts';
import axios from 'axios';
import { useToast } from '../../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COLORS = ['#1db584', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6'];
const PHASE_COLORS = {
  preparation: '#3b82f6',
  execution: '#1db584',
  facturation: '#f59e0b',
  termine: '#22c55e',
  annulee: '#ef4444'
};

const PHASE_LABELS = {
  preparation: 'Préparation',
  execution: 'Exécution',
  facturation: 'Facturation',
  termine: 'Terminé',
  annulee: 'Annulé'
};

const OperationalKPIs = ({ period = '30d', token }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { toast } = useToast();

  const fetchData = useCallback(async () => {
    if (!token) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.get(`${API}/admin/dashboard/operational-kpis`, {
        params: { period }});
      setData(response.data);
    } catch (err) {
      console.error('Error fetching operational KPIs:', err);
      setError(err.response?.data?.detail || 'Erreur lors du chargement');
      toast({
        title: "Erreur",
        description: "Impossible de charger les KPIs opérationnels",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  }, [period, token, toast]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  if (error) {
    return (
      <Card className="border-red-200 bg-red-50">
        <CardContent className="p-6">
          <div className="flex items-center gap-2 text-red-600">
            <AlertTriangle className="h-5 w-5" />
            <span>{error}</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!data) return null;

  // Prepare chart data
  const loadDistributionData = Object.entries(data.truckLoadRate?.distribution || {}).map(([key, value]) => ({
    name: key,
    value: value
  }));

  const deliveryDistributionData = Object.entries(data.deliveryTime?.distribution || {}).map(([key, value]) => ({
    name: key,
    value: value
  }));

  const phaseData = (data.phaseTracking || []).map(p => ({
    name: PHASE_LABELS[p.phase] || p.phase,
    value: p.count,
    percent: p.percent,
    fill: PHASE_COLORS[p.phase] || '#71717a'
  }));

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <Activity className="h-5 w-5 text-emerald-600" />
            KPIs Opérationnels
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Suivi de la performance opérationnelle • {data.totalOperations} opérations
          </p>
        </div>
        <button 
          onClick={fetchData}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          title="Actualiser"
        >
          <RefreshCw className="h-4 w-4 text-gray-500" />
        </button>
      </div>

      {/* KPI Cards Row — 3 cards (Total opérations removed: already in overview banner) */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Taux de chargement moyen */}
        <Card className="border-0 shadow-sm bg-gradient-to-br from-blue-50 to-white">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Taux de chargement moyen</p>
                <p className="text-3xl font-bold text-blue-600 mt-1">
                  {data.truckLoadRate?.average || 0}%
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  Capacité: {data.truckLoadRate?.truckCapacityUsed || 25}T/camion
                </p>
              </div>
              <div className="p-3 bg-blue-100 rounded-xl">
                <Truck className="h-6 w-6 text-blue-600" />
              </div>
            </div>
            <Progress 
              value={data.truckLoadRate?.average || 0} 
              className="mt-4 h-2"
            />
          </CardContent>
        </Card>

        {/* Délai moyen de livraison */}
        <Card className="border-0 shadow-sm bg-gradient-to-br from-emerald-50 to-white">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Délai moyen de livraison</p>
                <p className="text-3xl font-bold text-emerald-600 mt-1">
                  {data.deliveryTime?.averageDays || 0}
                  <span className="text-lg font-normal text-gray-500"> j</span>
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {data.deliveryTime?.totalDelivered || 0} livraisons terminées
                </p>
              </div>
              <div className="p-3 bg-emerald-100 rounded-xl">
                <Clock className="h-6 w-6 text-emerald-600" />
              </div>
            </div>
            <div className="mt-4 flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-emerald-500" />
              <span className="text-sm text-gray-600">
                {data.deliveryTime?.onTimeRate || 0}% livrées à temps
              </span>
            </div>
          </CardContent>
        </Card>

        {/* Opérations en retard */}
        <Card className={`border-0 shadow-sm ${data.lateOperations?.rate > 20 ? 'bg-gradient-to-br from-red-50 to-white' : 'bg-gradient-to-br from-amber-50 to-white'}`}>
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Opérations en retard</p>
                <p className={`text-3xl font-bold mt-1 ${data.lateOperations?.rate > 20 ? 'text-red-600' : 'text-amber-600'}`}>
                  {data.lateOperations?.count || 0}
                  <span className="text-base font-normal text-gray-500 ml-1">/ {data.lateOperations?.activeOperations || 0} actives</span>
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  Taux : {data.lateOperations?.rate || 0}%
                </p>
              </div>
              <div className={`p-3 rounded-xl ${data.lateOperations?.rate > 20 ? 'bg-red-100' : 'bg-amber-100'}`}>
                <AlertTriangle className={`h-6 w-6 ${data.lateOperations?.rate > 20 ? 'text-red-600' : 'text-amber-600'}`} />
              </div>
            </div>
            <div className="mt-4">
              <Badge variant={data.lateOperations?.rate > 20 ? "destructive" : "secondary"}>
                {data.lateOperations?.rate > 20 ? '⚠ Attention' : '✓ Acceptable'}
              </Badge>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Distribution taux de chargement */}
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <Truck className="h-4 w-4 text-blue-600" />
              Distribution du taux de chargement
            </CardTitle>
            <CardDescription>Répartition par tranche de capacité utilisée</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={loadDistributionData} layout="vertical" margin={{ top: 4, right: 55, left: 8, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
                  <YAxis type="category" dataKey="name" width={80} tick={{ fontSize: 12, fill: '#374151' }} axisLine={false} tickLine={false} />
                  <Tooltip
                    formatter={(value) => [`${value} opération${value > 1 ? 's' : ''}`, 'Volume']}
                    contentStyle={{ backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '8px', fontSize: 12 }}
                  />
                  <Bar dataKey="value" fill="#3b82f6" radius={[0, 6, 6, 0]} maxBarSize={28}>
                    <LabelList dataKey="value" position="right" style={{ fontSize: 12, fontWeight: 600, fill: '#374151' }} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Distribution des délais — remplacée par barres horizontales (plus lisible que pie) */}
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <Clock className="h-4 w-4 text-emerald-600" />
              Distribution des délais de livraison
            </CardTitle>
            <CardDescription>Répartition par durée · en nombre d'opérations</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={deliveryDistributionData} layout="vertical" margin={{ top: 4, right: 55, left: 8, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
                  <YAxis type="category" dataKey="name" width={80} tick={{ fontSize: 12, fill: '#374151' }} axisLine={false} tickLine={false} />
                  <Tooltip
                    formatter={(value) => [`${value} opération${value > 1 ? 's' : ''}`, 'Volume']}
                    contentStyle={{ backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '8px', fontSize: 12 }}
                  />
                  <Bar dataKey="value" radius={[0, 6, 6, 0]} maxBarSize={28}>
                    {deliveryDistributionData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                    <LabelList dataKey="value" position="right" style={{ fontSize: 12, fontWeight: 600, fill: '#374151' }} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Status Tracking */}
      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <Activity className="h-4 w-4 text-purple-600" />
            Suivi des opérations par phase
          </CardTitle>
          <CardDescription>Vue d'ensemble du pipeline opérationnel</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
            {phaseData.map((phase, index) => (
              <div 
                key={phase.name}
                className="p-4 rounded-xl text-center"
                style={{ backgroundColor: `${phase.fill}15` }}
              >
                <p className="text-2xl font-bold" style={{ color: phase.fill }}>
                  {phase.value}
                </p>
                <p className="text-sm text-gray-600 mt-1">{phase.name}</p>
                <p className="text-xs text-gray-500">{phase.percent}%</p>
              </div>
            ))}
          </div>
          
          {/* Timeline Chart */}
          {data.statusTimeline && data.statusTimeline.length > 0 && (
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={data.statusTimeline}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="dayName" fontSize={12} />
                  <YAxis fontSize={12} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'white', 
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px'
                    }}
                  />
                  <Legend />
                  <Line type="monotone" dataKey="preparation" name="Préparation" stroke={PHASE_COLORS.preparation} strokeWidth={2} />
                  <Line type="monotone" dataKey="execution" name="Exécution" stroke={PHASE_COLORS.execution} strokeWidth={2} />
                  <Line type="monotone" dataKey="termine" name="Terminé" stroke={PHASE_COLORS.termine} strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Late Operations List */}
      {data.lateOperations?.topLate && data.lateOperations.topLate.length > 0 && (
        <Card className="border-0 shadow-sm border-l-4 border-l-amber-500">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-600" />
              Opérations en retard
            </CardTitle>
            <CardDescription>Top {data.lateOperations.topLate.length} opérations les plus en retard</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {data.lateOperations.topLate.slice(0, 5).map((op, index) => (
                <div 
                  key={op.operationNumber}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <span className="text-lg font-bold text-gray-400">#{index + 1}</span>
                    <div>
                      <p className="font-medium text-gray-900">{op.operationNumber}</p>
                      <p className="text-sm text-gray-500">
                        {op.departure} → {op.arrival}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="flex items-center gap-1 justify-end mb-1">
                      <Badge variant="destructive">
                        +{op.daysLate}j retard
                      </Badge>
                      {op.slaLabel && (
                        <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium">{op.slaLabel}</span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500">
                      {op.operationType && <span className="capitalize mr-1">{op.operationType}</span>}
                      · Prévu: {op.requestedDate}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Volume par jour de la semaine */}
      {data.volumeByWeekday && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card className="border-0 shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <Activity className="h-4 w-4 text-blue-600" />Volume par jour de la semaine
              </CardTitle>
              <CardDescription>Jours les plus chargés</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-44">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.volumeByWeekday} margin={{ top: 4, right: 8, left: 0, bottom: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="day" tick={{ fontSize: 10, fill: '#9ca3af' }} tickLine={false} axisLine={false} tickFormatter={d => d.slice(0,3)} />
                    <YAxis tick={{ fontSize: 10, fill: '#9ca3af' }} tickLine={false} axisLine={false} width={28} />
                    <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8, border: '1px solid #e5e7eb' }} />
                    <Bar dataKey="count" fill="#3b82f6" radius={[4,4,0,0]} name="Opérations">
                      <LabelList dataKey="count" position="top" style={{ fontSize: 10, fill: '#6b7280', fontWeight: 600 }} />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Volume par mois */}
          {data.volumeByMonth && data.volumeByMonth.length > 0 && (
            <Card className="border-0 shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                  <TrendingUp className="h-4 w-4 text-emerald-600" />Volume mensuel (12 derniers mois)
                </CardTitle>
                <CardDescription>Nombre d'opérations par mois</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-44">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={data.volumeByMonth} margin={{ top: 4, right: 8, left: 0, bottom: 4 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#9ca3af' }} tickLine={false} axisLine={false} />
                      <YAxis tick={{ fontSize: 10, fill: '#9ca3af' }} tickLine={false} axisLine={false} width={28} />
                      <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8, border: '1px solid #e5e7eb' }} />
                      <Bar dataKey="count" fill="#1db584" radius={[4,4,0,0]} name="Opérations" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Top Routes */}
      {(data.topRoutesByVolume || data.topRoutesByRevenue) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {data.topRoutesByVolume && data.topRoutesByVolume.length > 0 && (
            <Card className="border-0 shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                  <Package className="h-4 w-4 text-indigo-600" />Top destinations — Volume
                </CardTitle>
                <CardDescription>Routes les plus fréquentées</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {data.topRoutesByVolume.map((r,i) => (
                    <div key={i} className="flex items-center gap-2 py-1.5 border-b border-gray-50 last:border-0">
                      <span className="text-xs font-bold text-gray-400 w-5">{i+1}</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-gray-800 truncate">{r.departure} → {r.destination}</p>
                        <div className="h-1.5 bg-gray-100 rounded mt-1 overflow-hidden">
                          <div className="h-full rounded bg-indigo-500" style={{ width: `${(r.count/data.topRoutesByVolume[0]?.count||1)*100}%` }} />
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <span className="text-xs font-bold text-gray-800">{r.count} ops</span>
                        <p className="text-xs text-gray-400">moy {(r.avgRevenue/1000).toFixed(0)}K</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {data.topRoutesByRevenue && data.topRoutesByRevenue.length > 0 && (
            <Card className="border-0 shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                  <TrendingUp className="h-4 w-4 text-emerald-600" />Top destinations — CA
                </CardTitle>
                <CardDescription>Routes les plus rentables</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {data.topRoutesByRevenue.map((r,i) => (
                    <div key={i} className="flex items-center gap-2 py-1.5 border-b border-gray-50 last:border-0">
                      <span className="text-xs font-bold text-gray-400 w-5">{i+1}</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-gray-800 truncate">{r.departure} → {r.destination}</p>
                        <div className="h-1.5 bg-gray-100 rounded mt-1 overflow-hidden">
                          <div className="h-full rounded bg-emerald-500" style={{ width: `${(r.revenue/data.topRoutesByRevenue[0]?.revenue||1)*100}%` }} />
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <span className="text-xs font-bold text-gray-800">{(r.revenue/1000).toFixed(0)}K</span>
                        <p className="text-xs text-gray-400">{r.count} ops</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Fast payers */}
      {data.fastPayers && data.fastPayers.length > 0 && (
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-emerald-600" />Clients qui paient le plus vite
            </CardTitle>
            <CardDescription>Délai moyen de paiement après facturation</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
              {data.fastPayers.map((p,i) => {
                const tagColor = p.tag === 'Excellent' ? 'bg-emerald-100 text-emerald-700' : p.tag === 'Bon' ? 'bg-blue-100 text-blue-700' : p.tag === 'Moyen' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700';
                return (
                  <div key={i} className="p-3 rounded-xl border border-gray-100 hover:border-gray-200 transition-colors text-center">
                    <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-2">
                      <span className="text-xs font-bold text-gray-600">{i+1}</span>
                    </div>
                    <p className="text-xs font-medium text-gray-800 truncate">{p.clientName || 'Inconnu'}</p>
                    <p className="text-lg font-bold text-gray-900 mt-1">{p.avgPaymentDays}j</p>
                    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium mt-1 ${tagColor}`}>{p.tag}</span>
                    <p className="text-xs text-gray-400 mt-1">{p.paymentCount} paiements</p>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Analyse temps perdu */}
      {data.timeLossAnalysis && data.timeLossAnalysis.length > 0 && (
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <Clock className="h-4 w-4 text-red-600" />Opérations avec le plus de retard
            </CardTitle>
            <CardDescription>Analyse des pertes de temps opérationnelles</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-gray-100">
                    <th className="text-left py-2 text-gray-500 font-medium">Référence</th>
                    <th className="text-left py-2 text-gray-500 font-medium">Route</th>
                    <th className="text-left py-2 text-gray-500 font-medium">Client</th>
                    <th className="text-center py-2 text-gray-500 font-medium">Type / SLA</th>
                    <th className="text-center py-2 text-gray-500 font-medium">Retard</th>
                    <th className="text-center py-2 text-gray-500 font-medium">Statut</th>
                  </tr>
                </thead>
                <tbody>
                  {data.timeLossAnalysis.map((op,i) => (
                    <tr key={i} className="border-b border-gray-50 hover:bg-gray-50">
                      <td className="py-2 font-medium text-gray-800">{op.operationNumber || '—'}</td>
                      <td className="py-2 text-gray-600">{op.departure || '?'} → {op.arrival || '?'}</td>
                      <td className="py-2 text-gray-600 truncate max-w-[100px]">{op.clientName || '—'}</td>
                      <td className="py-2 text-center">
                        <div className="flex flex-col items-center gap-0.5">
                          <span className="text-xs text-gray-500 capitalize">{op.operationType || '—'}</span>
                          {op.slaLabel && <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">{op.slaLabel}</span>}
                        </div>
                      </td>
                      <td className="py-2 text-center">
                        <span className={`px-2 py-0.5 rounded-full font-bold ${op.daysLate > 7 ? 'bg-red-100 text-red-700' : op.daysLate > 3 ? 'bg-orange-100 text-orange-700' : 'bg-yellow-100 text-yellow-700'}`}>
                          +{op.daysLate}j
                        </span>
                      </td>
                      <td className="py-2 text-center text-gray-500">{op.status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default OperationalKPIs;
